/**
 * The platform seam for local-disk backup.
 *
 * WHY AN INTERFACE
 * ----------------
 * "Write to a folder" means three different things depending on where the app
 * runs, and the engine that decides WHAT to write must not know which:
 *
 *   - Browser (PWA): the File System Access API. A `FileSystemDirectoryHandle`
 *     the user picks, whose write permission must be re-requested each session
 *     and can be revoked by the OS at any time.
 *   - Electron: the main process over an allow-listed IPC channel. The renderer
 *     has no filesystem access; every operation is an explicit `invoke`.
 *   - Tests / unsupported browsers: an in-memory target, so the engine is proven
 *     without a real disk and so an unsupported browser degrades to a clear
 *     "not supported" rather than a broken promise.
 *
 * The engine depends only on this interface, so the same backup and restore
 * logic runs identically on all three — and a test can simulate disk-full,
 * permission-denied and interrupted writes by making an implementation throw or
 * truncate, which a real disk will not do on demand.
 *
 * WHY ATOMIC WRITES ARE THE TARGET'S JOB
 * --------------------------------------
 * The manifest is the one file that must never be seen half-written: it is what
 * recovery trusts. Each target implements `writeFileAtomic` (write a temp file,
 * then rename over the destination) so the engine never has to reason about
 * platform-specific rename semantics. Journal appends are the opposite — they
 * MUST be a plain append, because rewriting the log is exactly the failure this
 * whole design avoids.
 */

/** A directory the user granted us, abstracted over the platform. */
export interface DiskBackupTarget {
  /** True when this target can actually reach a folder on this platform. */
  readonly supported: boolean;

  /** Ask the user to pick (or re-authorise) a folder. Resolves to its label. */
  pickFolder(): Promise<string>;

  /**
   * Re-establish access to a previously picked folder without prompting, when
   * the platform allows it (File System Access re-checks a saved handle).
   * Returns false when re-authorisation needs a fresh `pickFolder()`.
   */
  ensurePermission(): Promise<boolean>;

  /**
   * Check whether the target is ready to write WITHOUT prompting the user.
   *
   * This is the non-blocking gate used by the automatic-backup scheduler: it must
   * never call `requestPermission`, which requires a user gesture and will fail
   * from a timer callback. Returns one of:
   *  - 'ready'            — folder is configured AND permission is granted
   *  - 'no_folder'        — no folder has been chosen yet (user must pick one)
   *  - 'permission_lost'  — folder exists but access needs re-authorisation
   *
   * The distinction between 'no_folder' and 'permission_lost' drives the UI: the
   * former shows "Turn on", the latter shows "Re-authorise".
   */
  checkReady(): Promise<BackupReadiness>;

  /** Read a file's text, or null when it does not exist. */
  readFile(name: string): Promise<string | null>;

  /** Read an existing file without requesting write permission. */
  readFileReadOnly?(name: string): Promise<string | null>;

  /** Append text to a file, creating it if absent. Never rewrites existing bytes. */
  appendFile(name: string, text: string): Promise<void>;

  /**
   * Write a file so it is either fully old or fully new — temp file + rename.
   * Used only for the manifest and for checkpoints.
   */
  writeFileAtomic(name: string, text: string): Promise<void>;

  /** List file names in the folder. Used to find checkpoints on restore. */
  listFiles(): Promise<readonly string[]>;

  /** A human label for the chosen folder, for the status line. */
  folderLabel(): string;
}

/** Whether the backup target is ready for automatic (timer-driven) writes. */
export type BackupReadiness = 'ready' | 'no_folder' | 'permission_lost';

/** Thrown by a target when the platform or the user forbids an operation. */
export class DiskBackupError extends Error {
  constructor(
    readonly code:
      | 'UNSUPPORTED'
      | 'PERMISSION_DENIED'
      | 'NOT_FOUND'
      | 'DISK_FULL'
      | 'IO_ERROR',
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'DiskBackupError';
  }
}

/**
 * An in-memory target for tests and for unsupported browsers.
 *
 * It faithfully models the two behaviours that matter and that a real disk will
 * not reproduce on demand:
 *   - `failNextAppend` / `failNextWrite` inject disk-full / IO errors;
 *   - `truncateLastAppend` leaves a half-written final line, the exact state of
 *     a process killed mid-flush.
 */
export class InMemoryDiskBackupTarget implements DiskBackupTarget {
  readonly supported = true;
  private readonly files = new Map<string, string>();
  private label = 'in-memory';

  private failNextAppend = false;
  private failNextWrite = false;
  private truncateNextAppendChars = 0;

  async pickFolder(): Promise<string> {
    this.label = 'in-memory';
    return this.label;
  }

  async ensurePermission(): Promise<boolean> {
    return true;
  }

  async checkReady(): Promise<BackupReadiness> {
    // The in-memory target always reports 'ready' so the scheduler can tick
    // against it in tests. In production, BrowserDiskBackupTarget overrides
    // this to check the real directory handle and OS permission state.
    return 'ready';
  }

  async readFile(name: string): Promise<string | null> {
    return this.files.has(name) ? (this.files.get(name) as string) : null;
  }

  async readFileReadOnly(name: string): Promise<string | null> {
    return this.readFile(name);
  }

  async appendFile(name: string, text: string): Promise<void> {
    if (this.failNextAppend) {
      this.failNextAppend = false;
      throw new DiskBackupError('DISK_FULL', 'The backup disk is full.');
    }
    if (this.truncateNextAppendChars > 0) {
      const keep = Math.max(0, text.length - this.truncateNextAppendChars);
      const existing = this.files.get(name) ?? '';
      this.files.set(name, existing + text.slice(0, keep));
      this.truncateNextAppendChars = 0;
      return;
    }
    const existing = this.files.get(name) ?? '';
    this.files.set(name, existing + text);
  }

  async writeFileAtomic(name: string, text: string): Promise<void> {
    if (this.failNextWrite) {
      this.failNextWrite = false;
      throw new DiskBackupError('IO_ERROR', 'Could not write the backup file.');
    }
    this.files.set(name, text);
  }

  async listFiles(): Promise<readonly string[]> {
    return [...this.files.keys()].sort();
  }

  folderLabel(): string {
    return this.label;
  }

  /* ── test controls ── */
  failNextAppendWithDiskFull(): void {
    this.failNextAppend = true;
  }
  failNextWriteWithIoError(): void {
    this.failNextWrite = true;
  }
  truncateNextAppend(chars: number): void {
    this.truncateNextAppendChars = chars;
  }
  /** Raw access, for assertions. */
  raw(name: string): string | undefined {
    return this.files.get(name);
  }
}
